import React, { useState, useEffect, useRef } from "react";
import {
  defaultScheduleData,
  fetchScheduleData,
  type ScheduleSlot,
  type Talk,
} from "../../../services/scheduleService";
import "../programacion.css";

const INFO_TITLE =
  /bienvenida|acreditaci|almuerzo|comida|descanso|foto de grupo/i;

const FOCUS_RING_DARK =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-Colors-blue-dark";

const formatClock = (value: string) => {
  const [hour = "0", minute = "00"] = value.split(":");
  const h = Number(hour);
  const m = (minute ?? "00").padStart(2, "0");
  return {
    iso: `${String(h).padStart(2, "0")}:${m}`,
    label: `${h}:${m}`,
  };
};

const toTimeRange = (value: string) => {
  const [startRaw, endRaw] = value.split(/\s*-\s*/).map((part) => part?.trim());
  const start = formatClock(startRaw ?? "");
  if (!endRaw) return { iso: start.iso, label: start.label };
  const end = formatClock(endRaw);
  return {
    iso: `${start.iso}/${end.iso}`,
    label: `${start.label}-${end.label}`,
  };
};

const formatSala = (room: string) => {
  const name = room.replace(/^sala\s+/i, "").trim();
  const titled = name
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return `SALA ${titled}`;
};

const speakerNames = (item: { speakers?: Talk["speakers"] }) =>
  (item.speakers ?? []).map((speaker) => speaker.name).join(", ");

const getRowKind = (slot: ScheduleSlot) => {
  if (slot.talks?.length) return "talks" as const;
  if (slot.speakers?.length) return "ancho" as const;
  if (
    slot.type === "break" ||
    slot.type === "post-event" ||
    INFO_TITLE.test(slot.title ?? "")
  ) {
    return "info" as const;
  }
  return "destacado" as const;
};

const Schedule: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hasScrolled, setHasScrolled] = useState(false);
  const [scheduleData, setScheduleData] =
    useState<ScheduleSlot[]>(defaultScheduleData);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTalk, setSelectedTalk] = useState<Talk | null>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  const closeTalkDialog = () => setSelectedTalk(null);

  const isSlotActive = (timeRange: string): boolean => {
    const now = currentTime;
    const eventDate = new Date(2026, 8, 18);
    const isEventDay =
      now.getDate() === eventDate.getDate() &&
      now.getMonth() === eventDate.getMonth() &&
      now.getFullYear() === eventDate.getFullYear();

    if (!isEventDay) return false;

    const [startStr, endStr] = timeRange.split(" - ");
    if (!endStr) return false;

    const [startHour, startMin] = startStr.split(":").map(Number);
    const [endHour, endMin] = endStr.split(":").map(Number);

    const startTime = new Date(now);
    startTime.setHours(startHour, startMin, 0, 0);
    const endTime = new Date(now);
    endTime.setHours(endHour, endMin, 0, 0);

    return now >= startTime && now <= endTime;
  };

  useEffect(() => {
    const loadSchedule = async () => {
      setIsLoading(true);
      const data = await fetchScheduleData();
      setScheduleData(data);
      setIsLoading(false);
    };

    loadSchedule();
    const interval = setInterval(loadSchedule, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedTalk) return;

    lastFocusRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeTalkDialog();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const nodes = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (nodes.length === 1) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "unset";
      lastFocusRef.current?.focus();
    };
  }, [selectedTalk]);

  useEffect(() => {
    if (hasScrolled) return;
    const activeIndex = scheduleData.findIndex((slot) =>
      isSlotActive(slot.time),
    );
    if (activeIndex === -1 || !slotRefs.current[activeIndex]) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    slotRefs.current[activeIndex]?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
    setHasScrolled(true);
  }, [hasScrolled, scheduleData, currentTime]);

  const renderTalkBox = (talk: Talk, idx: number, isAncho: boolean) => {
    const ponentes = speakerNames(talk);
    const hasDescription = Boolean(talk.description);
    const className = `c-programa__box c-programa__box--charla${
      isAncho ? " c-programa__box--ancho" : ""
    }${talk.disabled ? " c-programa__box--disabled" : ""}`;

    const content = (
      <>
        {!isAncho && talk.room && (
          <p className="sala">{formatSala(talk.room)}</p>
        )}
        {talk.title && <p className="titulo">{talk.title}</p>}
        {ponentes && <p className="ponente">{ponentes}</p>}
      </>
    );

    if (talk.disabled) {
      return (
        <div
          key={`charla-${talk.room}-${idx}`}
          className={className}
          role="cell"
          aria-disabled="true"
        >
          {content}
        </div>
      );
    }

    if (hasDescription) {
      return (
        <button
          key={`charla-${talk.room}-${idx}`}
          type="button"
          className={`${className} c-programa__box--clickable`}
          role="cell"
          onClick={() => setSelectedTalk(talk)}
          aria-haspopup="dialog"
        >
          {content}
          <span className="sr-only">. Ver descripción.</span>
        </button>
      );
    }

    return (
      <div
        key={`charla-${talk.room}-${idx}`}
        className={className}
        role="cell"
        aria-colspan={isAncho ? 4 : undefined}
      >
        {content}
      </div>
    );
  };

  const renderTalkBoxes = (slot: ScheduleSlot) => {
    const talks = slot.talks ?? [];
    const assigned = talks.filter((talk) => !talk.disabled);
    const isAncho = assigned.length === 1;

    return (
      <div className="c-programa__grid">
        {talks.map((talk, idx) => {
          if (!talk.title && !talk.speakers?.length) {
            return (
              <div
                key={`open-${talk.room}-${idx}`}
                className="c-programa__box c-programa__box--open"
                role="cell"
                aria-label="Espacio abierto pendiente de asignar"
              >
                OPEN
              </div>
            );
          }
          return renderTalkBox(talk, idx, isAncho);
        })}
      </div>
    );
  };

  const renderBand = (slot: ScheduleSlot, kind: "info" | "destacado") => (
    <div
      className={`c-programa__box c-programa__box--${kind}`}
      role="cell"
      aria-colspan={4}
    >
      <h2>{slot.title}</h2>
      {kind === "info" && slot.mapLink && (
        <p className="meta">
          {slot.location}
          {" · "}
          <a href={slot.mapLink} target="_blank" rel="noopener noreferrer">
            Ver en mapa
            <span className="sr-only">
              {" "}
              (se abre en Google Maps, pestaña nueva)
            </span>
          </a>
        </p>
      )}
    </div>
  );

  const renderKeynote = (slot: ScheduleSlot) => {
    const ponentes = speakerNames(slot);

    return (
      <div
        className="c-programa__box c-programa__box--ancho"
        role="cell"
        aria-colspan={4}
      >
        {slot.title && <h2 className="titulo">{slot.title}</h2>}
        {ponentes && <p className="ponente">{ponentes}</p>}
      </div>
    );
  };

  return (
    <>
      {selectedTalk && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={closeTalkDialog}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              selectedTalk.title ? "talk-dialog-title" : undefined
            }
            aria-label={
              selectedTalk.title ? undefined : "Descripción de la charla"
            }
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <header className="flex justify-between items-start gap-4 mb-4">
                <div className="flex-1">
                  <p className="text-xs md:text-sm font-semibold uppercase text-[#BA2E25] mb-2">
                    {formatSala(selectedTalk.room)}
                  </p>
                  {selectedTalk.title && (
                    <h2
                      id="talk-dialog-title"
                      className="text-Colors-blue-dark text-xl md:text-2xl font-bold"
                    >
                      {selectedTalk.title}
                    </h2>
                  )}
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={closeTalkDialog}
                  className={`flex-shrink-0 text-Colors-blue-dark hover:text-[#BA2E25] hover:bg-gray-100 transition-all rounded-full flex items-center justify-center w-10 h-10 md:w-12 md:h-12 ${FOCUS_RING_DARK}`}
                  aria-label="Cerrar descripción de la charla"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={3.5}
                    stroke="currentColor"
                    className="w-6 h-6 md:w-7 md:h-7"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </header>

              {selectedTalk.description && (
                <p className="text-Colors-blue-dark text-base md:text-lg leading-relaxed mb-6">
                  {selectedTalk.description}
                </p>
              )}

              {selectedTalk.speakers && selectedTalk.speakers.length > 0 && (
                <section
                  aria-labelledby="talk-speakers-heading"
                  className="border-t border-gray-200 pt-6"
                >
                  <h3
                    id="talk-speakers-heading"
                    className="text-Colors-blue-dark text-sm font-semibold mb-3"
                  >
                    {selectedTalk.speakers.length === 1
                      ? "Ponente"
                      : "Ponentes"}
                  </h3>
                  <ul className="flex flex-wrap gap-3">
                    {selectedTalk.speakers.map((speaker) => (
                      <li
                        key={speaker.name}
                        className="flex items-center gap-3 bg-Colors-blue-light/10 px-4 py-2 rounded-full"
                      >
                        {speaker.photo && (
                          <img
                            src={speaker.photo}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border-2 border-Colors-blue-dark/60"
                          />
                        )}
                        <span className="text-Colors-blue-dark text-sm md:text-base font-medium">
                          {speaker.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="p-programa">
        <div className="c-decor-hex c-decor-hex--header" aria-hidden="true" />

        <div className="p-programa__container">
          <h1 className="sr-only">Agenda del Open Space by Jakala</h1>
          {isLoading && (
            <p role="status" aria-live="polite" className="c-programa-status">
              Actualizando programación...
            </p>
          )}

          <div
            className="c-programa-tabla"
            role="table"
            aria-label="Agenda del Open Space by Jakala"
          >
            {scheduleData.map((slot, index) => {
              const isActive = isSlotActive(slot.time);
              const { iso, label } = toTimeRange(slot.time);
              const kind = getRowKind(slot);

              return (
                <div
                  key={`${slot.time}-${slot.type}-${index}`}
                  ref={(el) => {
                    slotRefs.current[index] = el;
                  }}
                  className="c-programa"
                  role="row"
                  aria-current={isActive ? "true" : undefined}
                >
                  <div className="c-programa__hora" role="rowheader">
                    <time dateTime={iso}>{label}</time>
                    {isActive && (
                      <span className="vivo" role="status">
                        EN VIVO
                      </span>
                    )}
                  </div>

                  {kind === "talks"
                    ? renderTalkBoxes(slot)
                    : kind === "ancho"
                      ? renderKeynote(slot)
                      : renderBand(slot, kind)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Schedule;
